import { useRef, useState } from "react";
import { staffApi, ApiError } from "@/api";
import { Button } from "@/components/ui";

const MAX_BYTES = 10 * 1024 * 1024; // Cloudflare single-POST limit (~10 MB)

interface CloudflareUploadResult {
  success: boolean;
  result?: { id: string; variants?: string[] };
  errors?: unknown;
}

/**
 * Reusable Cloudflare Images uploader (§6) — powers the image block, split
 * image, author photo, and background image. The account token never touches
 * the browser: we mint a one-time direct-upload URL (api 203), then POST the
 * file straight to it. If Cloudflare isn't configured, shows a friendly notice;
 * the rest of the builder works regardless.
 */
export function ImageUploader({
  value,
  onChange,
  label = "Image",
}: {
  value?: string;
  onChange: (url: string) => void;
  label?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function handleFile(file: File) {
    setError(undefined);
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Image is too large (max 10 MB).");
      return;
    }

    setBusy(true);
    try {
      // 1. Mint a one-time upload URL (staff-authed).
      const { uploadURL } = await staffApi.mintImageUploadUrl();

      // 2. Upload the file directly to Cloudflare — NO auth header; the URL is
      //    pre-authorised.
      const form = new FormData();
      form.append("file", file);
      const up = await fetch(uploadURL, { method: "POST", body: form });
      if (!up.ok) throw new Error(`Upload failed (${up.status}).`);
      const result = (await up.json()) as CloudflareUploadResult;

      const deliveryUrl = result.result?.variants?.[0];
      if (!result.success || !deliveryUrl) {
        throw new Error("Upload did not return an image URL.");
      }
      // 3. Store the delivery URL.
      onChange(deliveryUrl);
    } catch (err) {
      if (err instanceof ApiError && (err.code === "not_configured" || /not configured|cloudflare/i.test(err.message))) {
        setError("Image uploads aren’t set up yet.");
      } else {
        setError(err instanceof Error ? err.message : "Could not upload the image.");
      }
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="uploader">
      <span className="uploader__label">{label}</span>
      {value ? (
        <div className="uploader__preview">
          <img src={value} alt="" />
          <div className="uploader__preview-actions">
            <Button variant="ghost" size="sm" onClick={() => inputRef.current?.click()} disabled={busy}>
              Replace
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onChange("")} disabled={busy}>
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="secondary" size="sm" onClick={() => inputRef.current?.click()} loading={busy}>
          Upload image
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />
      {error ? <p className="uploader__error">{error}</p> : null}
    </div>
  );
}
